import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Target,
  Scales,
  Clock,
  PersonSimpleRun,
  PersonSimpleWalk,
  Barbell,
  Bicycle,
  TrendUp,
  Heartbeat,
  Lightning,
  ArrowRight,
  ArrowLeft,
  Check
} from '@phosphor-icons/react';

const MACHINES = [
  { id: 'treadmill', label: 'Treadmill', Icon: PersonSimpleRun },
  { id: 'elliptical', label: 'Elliptical', Icon: Heartbeat },
  { id: 'stair_climber', label: 'Stair Climber', Icon: TrendUp },
  { id: 'row_machine', label: 'Row Machine', Icon: Barbell },
  { id: 'running', label: 'Running', Icon: Lightning },
  { id: 'speed_walking', label: 'Speed Walking', Icon: PersonSimpleWalk },
  { id: 'stationary_bike', label: 'Stationary Bike', Icon: Bicycle },
];

const Onboarding = () => {
  const navigate = useNavigate();
  const { updateUser, token } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    cardio_goal: '',
    fitness_level: 'beginner',
    current_weight: '',
    goal_weight: '',
    days_per_week: 3,
    favorite_machine: '',
  });

  const totalSteps = 3;

  const updateForm = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleNext = () => {
    if (step < totalSteps) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.put(`${API}/profile`, {
        ...formData,
        current_weight: formData.current_weight ? parseFloat(formData.current_weight) : null,
        goal_weight: formData.goal_weight ? parseFloat(formData.goal_weight) : null,
      }, {
        withCredentials: true,
        headers
      });
      updateUser(response.data.user);
      toast.success('Profile setup complete!');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-background flex flex-col p-6 relative overflow-hidden hexagon-bg"
      data-testid="onboarding-screen"
    >
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#D4AF37]/5 blur-[100px] rounded-full" />

      {/* Progress bar */}
      <div className="max-w-md w-full mx-auto mb-8">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-muted-foreground uppercase tracking-wider">Step {step} of {totalSteps}</span>
          <span className="text-xs text-[#D4AF37]">{Math.round((step / totalSteps) * 100)}%</span>
        </div>
        <div className="h-1 bg-card rounded-full overflow-hidden">
          <div
            className="h-full bg-[#D4AF37] transition-all duration-500"
            style={{ width: `${(step / totalSteps) * 100}%` }}
          />
        </div>
      </div>

      <div className="flex-1 max-w-md w-full mx-auto relative z-10">
        {/* Step 1: Goal & Fitness Level */}
        {step === 1 && (
          <div className="animate-fade-in-up">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-[#D4AF37]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Target weight="duotone" className="w-8 h-8 text-[#D4AF37]" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                What's Your Goal?
              </h2>
              <p className="text-muted-foreground">Let's personalize your 365-day journey</p>
            </div>

            <div className="space-y-5">
              <div>
                <Label className="text-sm text-muted-foreground mb-2 block">Primary Goal</Label>
                <Select value={formData.cardio_goal} onValueChange={(v) => updateForm('cardio_goal', v)}>
                  <SelectTrigger className="bg-card border-border rounded-xl py-6" data-testid="goal-select">
                    <SelectValue placeholder="Select your main goal" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="lose_weight">Lose Weight</SelectItem>
                    <SelectItem value="build_endurance">Build Endurance</SelectItem>
                    <SelectItem value="improve_health">Improve Overall Health</SelectItem>
                    <SelectItem value="stress_relief">Stress Relief</SelectItem>
                    <SelectItem value="stay_consistent">Stay Consistent</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm text-muted-foreground mb-2 block">Fitness Level</Label>
                <Select value={formData.fitness_level} onValueChange={(v) => updateForm('fitness_level', v)}>
                  <SelectTrigger className="bg-card border-border rounded-xl py-6" data-testid="fitness-level-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="beginner">Beginner</SelectItem>
                    <SelectItem value="intermediate">Intermediate</SelectItem>
                    <SelectItem value="advanced">Advanced</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Weight */}
        {step === 2 && (
          <div className="animate-fade-in-up">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-[#D4AF37]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Scales weight="duotone" className="w-8 h-8 text-[#D4AF37]" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Your Weight Goals
              </h2>
              <p className="text-muted-foreground">Track your transformation (optional)</p>
            </div>

            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-muted-foreground mb-2 block">Starting Weight (lbs)</Label>
                  <Input
                    type="number"
                    value={formData.current_weight}
                    onChange={(e) => updateForm('current_weight', e.target.value)}
                    placeholder="150"
                    className="bg-card border-border rounded-xl py-6"
                    data-testid="current-weight-input"
                  />
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground mb-2 block">Goal Weight (lbs)</Label>
                  <Input
                    type="number"
                    value={formData.goal_weight}
                    onChange={(e) => updateForm('goal_weight', e.target.value)}
                    placeholder="140"
                    className="bg-card border-border rounded-xl py-6"
                    data-testid="goal-weight-input"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Frequency & Favorite Machine */}
        {step === 3 && (
          <div className="animate-fade-in-up">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-[#D4AF37]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Clock weight="duotone" className="w-8 h-8 text-[#D4AF37]" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Your Workout Plan
              </h2>
              <p className="text-muted-foreground">How often and what's your favorite?</p>
            </div>

            <div className="space-y-6">
              <div>
                <Label className="text-sm text-muted-foreground mb-3 block">Days Per Week</Label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5, 6, 7].map((day) => (
                    <button
                      key={day}
                      onClick={() => updateForm('days_per_week', day)}
                      className={`flex-1 py-3 rounded-xl text-sm font-bold transition-all ${
                        formData.days_per_week === day
                          ? 'bg-[#D4AF37] text-black'
                          : 'bg-card border border-border text-muted-foreground hover:border-[#D4AF37]/50'
                      }`}
                      data-testid={`days-${day}`}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-sm text-muted-foreground mb-3 block">Favorite Machine</Label>
                <div className="grid grid-cols-2 gap-2">
                  {MACHINES.map(({ id, label, Icon }) => (
                    <button
                      key={id}
                      onClick={() => updateForm('favorite_machine', id)}
                      className={`p-3 rounded-xl border flex items-center gap-2 transition-all text-left ${
                        formData.favorite_machine === id
                          ? 'bg-[#D4AF37]/10 border-[#D4AF37] text-foreground'
                          : 'bg-card border-border text-muted-foreground hover:border-[#D4AF37]/50'
                      }`}
                      data-testid={`fav-machine-${id}`}
                    >
                      <Icon weight={formData.favorite_machine === id ? 'fill' : 'duotone'} className={`w-5 h-5 ${formData.favorite_machine === id ? 'text-[#D4AF37]' : ''}`} />
                      <span className="text-xs font-medium">{label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Navigation buttons */}
        <div className="flex gap-4 mt-10">
          {step > 1 && (
            <Button
              onClick={handleBack}
              variant="outline"
              className="flex-1 border-border text-foreground rounded-full py-6"
              data-testid="back-btn"
            >
              <ArrowLeft weight="bold" className="w-5 h-5 mr-2" />
              Back
            </Button>
          )}

          {step < totalSteps ? (
            <Button
              onClick={handleNext}
              className="flex-1 bg-[#D4AF37] text-black font-bold uppercase tracking-widest rounded-full py-6 hover:bg-[#F3E5AB] transition-all gold-glow"
              data-testid="next-btn"
            >
              Next
              <ArrowRight weight="bold" className="w-5 h-5 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleComplete}
              disabled={loading}
              className="flex-1 bg-[#D4AF37] text-black font-bold uppercase tracking-widest rounded-full py-6 hover:bg-[#F3E5AB] transition-all gold-glow"
              data-testid="complete-btn"
            >
              {loading ? 'Saving...' : 'Start Journey'}
              <Check weight="bold" className="w-5 h-5 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
